import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useIsAdminOrganization } from '@hero/hooks';
import { Table, UserName } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetOrganizationUsers } from '@hero/services/organization-service/hooks';

import { ActionCell } from './ActionCell';
import { Filter } from './Filter';
import { SecondHead } from './SecondHead';
import { StatusCell } from './StatusCell';
import { parseRole } from './utils';

function OrganizationUsersTable() {
  const { t } = useTranslation();
  const isOrganizationAdmin = useIsAdminOrganization();

  const [globalFilters, setGlobalFilters] = useState({
    searchTerm: '',
    filters: {
      role: [],
      status: []
    }
  });
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const { data: organizationUsers, isLoading } = useGetOrganizationUsers({
    filters: {
      searchTerm: globalFilters.searchTerm,
      roles: globalFilters.filters?.role.map(filter => filter.value),
      statuses: globalFilters.filters?.status.map(filter => filter.value)
    },
    pagination: {
      page: pageIndex + 1,
      limit: pageSize
    }
  });

  function firstNameCell({
    row: {
      original: { code, firstName, lastName, role }
    }
  }) {
    return (
      <UserName
        firstName={firstName}
        lastName={lastName}
        hasNameAvatar
        {...(role !== isOrganizationAdmin && {
          as: Link,
          to: ROUTE_PATHS.SETTINGS_USERS_DETAIL(code)
        })}
      />
    );
  }

  const columns = useMemo(
    () => [
      {
        header: t('name'),
        cell: firstNameCell
      },
      {
        id: 'email',
        header: t('email'),
        accessorKey: 'email'
      },
      {
        id: 'role',
        header: t('role'),
        accessorFn: val => {
          return t(parseRole(val.role), { count: 1 });
        }
      },
      {
        header: t('status'),
        accessorKey: 'status',
        cell: StatusCell
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ActionCell
      }
    ],
    []
  );

  return (
    <Table
      data={organizationUsers?.docs || []}
      columns={columns}
      globalFilters={globalFilters}
      pagination={{
        pageIndex,
        pageSize,
        total: organizationUsers?.pagination?.total
      }}
      manualPagination
      loading={isLoading}
      setGlobalFilters={getGlobalFilters => {
        const newGlobalFilters = getGlobalFilters(globalFilters);
        setGlobalFilters(newGlobalFilters);
      }}
      setPagination={setPagination}
      filterOptions={globalFilters.filters}
      paginationProps={{
        perPageSelection: true
      }}
      pageCount={organizationUsers?.pagination?.pages}
      enabled={{ filter: true, header: true, perPageSelection: true }}
      rowHeight="52px"
      translations={{
        filteredWithText: t('filteredUserWithText', {
          count: organizationUsers?.pagination?.total || '0'
        })
      }}
      filterProps={{
        placeholder: t('filterByType', { type: t('role') }),
        searchPlaceholder: t('searchRoleOnFilterPlaceholder')
      }}
      components={{
        SecondHead,
        Filter
      }}
      emptyTableContent={{
        title: 'Sorry, we couldn’t find any users that matched your search.',
        hasDescription: true,
        description: 'Please check your spelling, or try searching a different name or email.'
      }}
    />
  );
}

export { OrganizationUsersTable };
